import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import NamedBox from '../atoms/NamedBox';
import Dialog from '../molecules/Dialog';
import PieChartComponent from '../atoms/PieChartComponent';
import { Box, CircularProgress, Grid } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {
  COLOR_BORDER,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_PURPLE,
  COLOR_TEXT,
  COLOR_WHITE,
  COLOR_BACKGROUND_WARM,
  COLOR_BACKGROUND_LIGHT,
  COLOR_LIGHT_GRAY,
  COLOR_DARK_GRAY,
} from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import MEDIA from '../styles/media';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ListItem from '../atoms/ListItem';
import SchoolIcon from '../icons/SchoolIcon';
import ClassIcon from '../icons/ClassIcon';
import TransferPortalIcon from '../icons/TransferPortalIcon';
import ProfilerIcon from '../icons/ProfilerIcon';
import RankingsIcon from '../icons/RankingsIcon';
import UsageIcon from '../icons/UsageIcon';
import AwardIcon from '../icons/AwardIcon';
import PFFCircleIcon from '../icons/PFFCircleIcon';
import { CollegeProductionRow, PlayerUsageData } from '../types/CollegeProductionData';
import PFFGameGradeLogTable, { PFFGameLogRow } from '../molecules/PFFGameGradeLogTable';
import Player from '../types/Player';
import HighSchool from '../types/HighSchool';
import College from '../types/College';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import SortArrowIcon from '../icons/SortArrowIcon';
import { useTableSort } from '../hooks/useTableSort';
import Tooltip from '../atoms/Tooltip';
import { PlayerCareerDataResponse } from '../types/PlayerCareer';
import BannerIcon from '../icons/BannerIcon';
import NotAvailable from '../atoms/NotAvailable';
import { capitalizeFirstLetter, formatDate } from '../services/player';
import { PlayerPFFGameLog } from '../types/PlayerPFFGameLog';
import CloseButton from '../atoms/CloseButton';
import { buildPlayerProfilerLinks, ProfilerLink } from '../services/playerProfilers';
import { buildPlayerRankingLinks, RankingLink } from '../services/playerRankings';
import { State } from '../redux/reducers';

interface ColDef {
  key: keyof CollegeProductionRow | string;
  label: string;
  format?: 'number' | 'percent' | 'decimal';
  highlightRel?: boolean;
}

type TabKey = 'passing' | 'rushing' | 'receiving' | 'defense';

const COMMON_COLS: ColDef[] = [
  { key: 'year', label: 'Year' },
  { key: 'teamName', label: 'Team' },
  { key: 'gamesPlayed', label: 'G' },
  { key: 'gamesStarted', label: 'GS' },
];

const PASSING_COLS: ColDef[] = [
  { key: 'passingComp', label: 'Comp' },
  { key: 'passingAtt', label: 'Att' },
  { key: 'passingCompletionPercent', label: '%', format: 'percent' },
  { key: 'passingYds', label: 'Yds' },
  { key: 'passingRelPassYds', label: 'Rel Pass Yds', format: 'percent', highlightRel: true },
  { key: 'passingTD', label: 'TDs' },
  { key: 'passingInt', label: 'Ints' },
  { key: 'passingYPA', label: 'YPA', format: 'decimal' },
  { key: 'passingYPG', label: 'YPG', format: 'decimal' },
  { key: 'passingRZComp', label: 'RZ Comp' },
  { key: 'passingRZAtt', label: 'RZ Att' },
  { key: 'passingRZTD', label: 'RZ TDs' },
  { key: 'passingRZInt', label: 'RZ Ints' },
  { key: 'passingIntPercent', label: 'Int %', format: 'percent' },
  { key: 'passingEffPercent', label: 'Eff %', format: 'percent' },
  { key: 'passingFD', label: 'FDs' },
];

const RUSHING_COLS: ColDef[] = [
  { key: 'rushingAtt', label: 'Att' },
  { key: 'rushingYds', label: 'Yds' },
  { key: 'rushingYPC', label: 'YPC', format: 'decimal' },
  { key: 'rushingYPG', label: 'YPG', format: 'decimal' },
  { key: 'rushingRelRushYds', label: 'Rel Rush Yds', format: 'percent', highlightRel: true },
  { key: 'rushingRelYPC', label: 'Rel YPC', format: 'percent', highlightRel: true },
  { key: 'rushingTD', label: 'TD' },
  { key: 'rushingEffRuns', label: 'Eff Runs' },
  { key: 'rushing5PlusRuns', label: '5+' },
  { key: 'rushing10PlusRuns', label: '10+' },
  { key: 'rushing15PlusRuns', label: '15+' },
  { key: 'rushing20PlusRuns', label: '20+' },
  { key: 'rushingFD', label: 'FD' },
];

const RECEIVING_COLS: ColDef[] = [
  { key: 'receiving', label: 'Rec' },
  { key: 'receivingTar', label: 'Tar' },
  { key: 'receivingYds', label: 'Yds' },
  { key: 'receivingYPC', label: 'YPC', format: 'decimal' },
  { key: 'receivingTD', label: 'TD' },
  { key: 'receivingYPG', label: 'YPG', format: 'decimal' },
  { key: 'receivingRZRec', label: 'RZ Rec' },
  { key: 'receivingRZTar', label: 'RZ Tar' },
  { key: 'receiving3rdDnRec', label: '3rd Dn Rec' },
  { key: 'receiving3rdDnTar', label: '3rd Dn Tar' },
  { key: 'receiving10PlusPercent', label: '10+ %', format: 'percent' },
  { key: 'receiving15PlusPercent', label: '15+ %', format: 'percent' },
  { key: 'receiving25PlusPercent', label: '25+ %', format: 'percent' },
  { key: 'receiving40PlusPercent', label: '40+ %', format: 'percent' },
  { key: 'receivingFD', label: 'FD' },
  { key: 'receivingDrop', label: 'Drop' },
];

const DEFENSE_COLS: ColDef[] = [
  { key: 'defenseSoloTackles', label: 'Solo' },
  { key: 'defenseAssistedTackles', label: 'Ast' },
  { key: 'defenseTFL', label: 'TFL', format: 'decimal' },
  { key: 'defenseTFLYds', label: 'TFL Yds' },
  { key: 'defenseSack', label: 'Sack', format: 'decimal' },
  { key: 'defenseSackYds', label: 'Sack Yds' },
  { key: 'defenseQBHurry', label: 'QBH' },
  { key: 'defensePassBreak', label: 'PB' },
  { key: 'defenseInt', label: 'Int' },
  { key: 'defenseIntRetYds', label: 'Int Ret Yds' },
  { key: 'defenseIntRetTD', label: 'Int Ret TD' },
  { key: 'defenseFR', label: 'FR' },
  { key: 'defenseFRYds', label: 'FR Yds' },
  { key: 'defenseFRTD', label: 'FR TD' },
  { key: 'defenseFF', label: 'FF' },
  { key: 'defenseSafety', label: 'Safety' },
  { key: 'defenseBlockKick', label: 'Block' },
];

const TAB_COLS: Record<TabKey, ColDef[]> = {
  passing: PASSING_COLS,
  rushing: RUSHING_COLS,
  receiving: RECEIVING_COLS,
  defense: DEFENSE_COLS,
};

const STICKY_YEAR_COL_WIDTH = 72;
const STICKY_TEAM_COL_WIDTH = 140;
const STICKY_G_COL_WIDTH = 52;
const STICKY_GS_COL_WIDTH = 52;

const STICKY_LEFT_COL_0 = 0;
const STICKY_LEFT_COL_1 = STICKY_LEFT_COL_0 + STICKY_YEAR_COL_WIDTH;
const STICKY_LEFT_COL_2 = STICKY_LEFT_COL_1 + STICKY_TEAM_COL_WIDTH;
const STICKY_LEFT_COL_3 = STICKY_LEFT_COL_2 + STICKY_G_COL_WIDTH;

function formatCellValue(value: number, col: ColDef): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (col.format === 'percent') return `${value}%`;
  if (col.format === 'decimal') return String(value);
  return String(Math.round(value));
}

function getCellValue(row: CollegeProductionRow | TotalsRow, key: string): number | string {
  const r = row as unknown as Record<string, number | string>;
  return r[key] != null ? r[key] : '—';
}

interface TotalsRow extends Partial<CollegeProductionRow> {
  _isTotals: true;
  year: number;
  teamName: string;
}

function computeTotals(rows: CollegeProductionRow[], cols: ColDef[]): TotalsRow {
  const totals: TotalsRow = { _isTotals: true, year: 0, teamName: 'Totals' };
  const sumKeys = new Set(['gamesPlayed', 'gamesStarted', 'passingComp', 'passingAtt', 'passingYds', 'passingTD', 'passingInt', 'passingRZComp', 'passingRZAtt', 'passingRZTD', 'passingRZInt', 'passingFD', 'rushingAtt', 'rushingYds', 'rushingTD', 'rushingEffRuns', 'rushing5PlusRuns', 'rushing10PlusRuns', 'rushing15PlusRuns', 'rushing20PlusRuns', 'rushingFD', 'receiving', 'receivingTar', 'receivingYds', 'receivingTD', 'receivingRZRec', 'receivingRZTar', 'receiving3rdDnRec', 'receiving3rdDnTar', 'receivingFD', 'receivingDrop', 'defenseSoloTackles', 'defenseAssistedTackles', 'defenseTFLYds', 'defenseSackYds', 'defenseQBHurry', 'defensePassBreak', 'defenseInt', 'defenseIntRetYds', 'defenseIntRetTD', 'defenseFR', 'defenseFRYds', 'defenseFRTD', 'defenseFF', 'defenseSafety', 'defenseBlockKick']);
  const tot = totals as unknown as Record<string, number>;
  const rowRec = (r: CollegeProductionRow) => r as unknown as Record<string, number>;
  for (const col of cols) {
    const k = col.key as string;
    if (sumKeys.has(k)) {
      tot[k] = rows.reduce((s, row) => s + (Number(rowRec(row)[k]) || 0), 0);
    }
  }
  const avgKeys: Record<string, { num: string; den: string }> = {
    passingCompletionPercent: { num: 'passingComp', den: 'passingAtt' },
    passingYPA: { num: 'passingYds', den: 'passingAtt' },
    passingYPG: { num: 'passingYds', den: 'gamesPlayed' },
    passingIntPercent: { num: 'passingInt', den: 'passingAtt' },
    rushingYPC: { num: 'rushingYds', den: 'rushingAtt' },
    rushingYPG: { num: 'rushingYds', den: 'gamesPlayed' },
    receivingYPC: { num: 'receivingYds', den: 'receiving' },
    receivingYPG: { num: 'receivingYds', den: 'gamesPlayed' },
    defenseTFL: { num: 'defenseTFL', den: 'gamesPlayed' },
    defenseSack: { num: 'defenseSack', den: 'gamesPlayed' },
  };
  for (const [outKey, { num, den }] of Object.entries(avgKeys)) {
    const n = rows.reduce((s, r) => s + (Number(rowRec(r)[num]) || 0), 0);
    const d = rows.reduce((s, r) => s + (Number(rowRec(r)[den]) || 0), 0);
    tot[outKey] = d ? Math.round((n / d) * 100) / 100 : 0;
  }
  const relKeys = ['passingRelPassYds', 'passingEffPercent', 'rushingRelRushYds', 'rushingRelYPC', 'receiving10PlusPercent', 'receiving15PlusPercent', 'receiving25PlusPercent', 'receiving40PlusPercent'];
  for (const k of relKeys) {
    const vals = rows.map((r) => rowRec(r)[k]).filter((v) => typeof v === 'number');
    tot[k] = vals.length ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : 0;
  }
  return totals;
}

export interface ProductionTableAction {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  isProfilerAction?: boolean;
}

interface CollegeNFLProductionTableProps {
  className?: string;
  player?: Player;
  data?: CollegeProductionRow[] | null;
  playerUsageData?: PlayerUsageData[] | null;
  playerPFFGameLogData?: PlayerPFFGameLog[] | null;
  isPlayerPFFGameLogLoading?: boolean;
  /** Action links on the right of the first header */
  actions?: ProductionTableAction[];
  playerCareerData?: PlayerCareerDataResponse | null;
  starterYear?: number | null;
}

interface CareerAchievement {
  year: string;
  icon: string;
  title: string;
  detail: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    fontFamily: FONT_PROXIMA_NOVA,
  },
  headerClassName: {
    flexWrap: 'wrap',
    alignItems: 'stretch',
    padding: 0,

    '@media (max-width: 1260px)': {
      display: 'block',
    },
  },
  headerName: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 5, 1, 2),
    fontFamily: FONT_PROXIMA_NOVA,
    fontWeight: 700,

    '@media (max-width: 1680px)': {
      padding: theme.spacing(1, 2),
    },
    
    [theme.breakpoints.down('md')]: {
      width: '100%',
      paddingTop: theme.spacing(2),
    },
  },
  headerInner: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(0, 2),

    '@media (max-width: 1260px)': {
      display: 'block',
    },
    
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  labelAndTabs: {
    display: 'flex',
    
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  tab: {
    padding: theme.spacing(0.75, 2),
    fontWeight: 600,
    color: COLOR_DARK_GRAY,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),

    '&:hover': {
      color: COLOR_TEXT,
    },

    '@media (max-width: 1680px)': {
      padding: theme.spacing(0.75, 1),
      fontSize: theme.typography.pxToRem(14),
    },
    
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0.75, 2),
    },
    
    [theme.breakpoints.down('xs')]: {
      flexGrow: 1,
      padding: theme.spacing(0.75, 1),
      fontSize: theme.typography.pxToRem(12),
    },
  },
  tabActive: {
    color: COLOR_TEXT,
    borderBottomColor: COLOR_ORANGE,
  },
  actions: {
    flexGrow: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 5),
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 3, 1, 0),
    fontSize: theme.typography.pxToRem(16),

    '@media (max-width: 1680px)': {
      gap: theme.spacing(1, 2.5),
      fontSize: theme.typography.pxToRem(14),
    },

    '@media (max-width: 1260px)': {
      overflow: 'auto',
      flexWrap: 'nowrap',
      justifyContent: 'flex-start',
      width: '100%',
      padding: theme.spacing(1.5, 2),
      borderTop: `2px solid ${COLOR_MIDDLE_GRAY}`,
    },
  },
  actionLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontFamily: FONT_PROXIMA_NOVA,
    fontWeight: 600,
    color: COLOR_BLUE,
    textDecoration: 'underline',
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_DARK_BLUE,
      textDecoration: 'underline',
    },

    '@media (max-width: 1260px)': {
      flexShrink: 0,
    },
  },
  actionLinkDisabled: {
    color: COLOR_DARK_GRAY,
    cursor: 'default',
    '&:hover': { textDecoration: 'none' },
  },
  actionIcon: {
    flexShrink: 0,
    
    '& svg': {
      display: 'block',
      maxWidth: 28,
      maxHeight: 28,
    },

    '@media (max-width: 1680px)': {
      '& svg': {
        maxWidth: 22,
        maxHeight: 22,
      },
    },
  },
  actionLabel: {
    '@media (max-width: 1260px)': {
      whiteSpace: 'nowrap',
    },
  },
  profilerActionWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),

    '@media (max-width: 1260px)': {
      flexShrink: 0,
    },
  },
  profilerMenuTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: COLOR_BLUE,
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    lineHeight: 1,

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  profilerMenuItem: {
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(13),
  },
  tableContainer: {
    maxHeight: 360,
    overflow: 'auto',
  },
  table: {
    width: '100%',
    minWidth: 960,
    borderCollapse: 'collapse' as const,
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),
  },
  thead: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    backgroundColor: COLOR_WHITE,
  },
  th: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    borderRight: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(0.75, 1),
    fontWeight: 700,
    color: COLOR_TEXT,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    backgroundColor: COLOR_WHITE,
    userSelect: 'none' as const,
  },
  thFirst: {
    textAlign: 'left' as const,
    minWidth: STICKY_YEAR_COL_WIDTH,
    width: STICKY_YEAR_COL_WIDTH,
  },
  thSecond: {
    textAlign: 'left' as const,
    minWidth: STICKY_TEAM_COL_WIDTH,
    width: STICKY_TEAM_COL_WIDTH,
  },
  thThird: {
    minWidth: STICKY_G_COL_WIDTH,
    width: STICKY_G_COL_WIDTH,
  },
  thFourth: {
    minWidth: STICKY_GS_COL_WIDTH,
    width: STICKY_GS_COL_WIDTH,
  },
  sortableHeader: {
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
    },
  },
  sortLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.25),
    color: COLOR_TEXT,

    '&:hover': {
      color: COLOR_TEXT,
    },
  },
  sortLabelIcon: {
    opacity: 1,
    color: COLOR_MIDDLE_GRAY,

    '& svg': {
      display: 'block',
      width: 9,
      height: 'auto',
    },
  },
  bodyRow: {
    backgroundColor: COLOR_BACKGROUND_LIGHT,
  },
  bodyRowAlt: {
    backgroundColor: COLOR_WHITE,
  },
  totalsRow: {
    backgroundColor: COLOR_MIDDLE_GRAY,
    fontWeight: 600,
  },
  td: {
    padding: theme.spacing(1.5, 1),
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    fontWeight: 600,
  },
  tdFirst: {
    textAlign: 'left' as const,
    fontWeight: 600,
  },
  tdSecond: {
    textAlign: 'left' as const,
  },
  thSticky: {
    position: 'sticky' as const,
    zIndex: 3,
    backgroundColor: COLOR_WHITE,
  },
  tdSticky: {
    position: 'sticky' as const,
    zIndex: 2,
    backgroundColor: COLOR_BACKGROUND_LIGHT,
  },
  tdStickyAlt: {
    backgroundColor: COLOR_WHITE,
  },
  tdStickyTotals: {
    backgroundColor: COLOR_MIDDLE_GRAY,
  },
  stickyCol0: {
    left: STICKY_LEFT_COL_0,
  },
  stickyCol1: {
    left: STICKY_LEFT_COL_1,
  },
  stickyCol2: {
    left: STICKY_LEFT_COL_2,
  },
  stickyCol3: {
    left: STICKY_LEFT_COL_3,
    boxShadow: `2px 0 0 ${COLOR_BORDER}`,
  },
  cellHighlight: {
    color: COLOR_PURPLE,
  },
  usageDialog: {
    overflow: 'auto',
    width: '96%',
    maxWidth: 1600,
    maxHeight: 'calc(100% - 40px)',
    borderRadius: 8,
  },
  usageYearsContainer: {
  },
  usageYearBlock: {
  },
  usageYearTitle: {
    padding: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: COLOR_BACKGROUND_WARM,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
  },
  usageRoot: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 1100,
    margin: '0 auto',
    padding: theme.spacing(5),
    boxSizing: 'border-box' as const,
    
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(3),
    },

    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 0),
    },
  },
  usageChartWrapper: {
    display: 'flex',

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  usageChart: {
    flexGrow: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 310,
  },
  usageLegend: {
    alignSelf: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: 350,
    maxHeight: 350,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(0, 2),
  },
  usageRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1, 2),
    minWidth: 0,
    borderRadius: 8,
    padding: theme.spacing(0.5),
    lineHeight: 1.2,
    transition: 'background-color 180ms ease, opacity 180ms ease',

    '&:last-child': {
      marginBottom: 0,
    },
  },
  usageRowHighlighted: {
    backgroundColor: COLOR_BACKGROUND_WARM,
  },
  usageRowDimmed: {
    opacity: 0.6,
  },
  usageRowLabel: {
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: COLOR_TEXT,
  },
  usageRowValue: {
    color: COLOR_TEXT,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  usageLegendDot: {
    flexShrink: 0,
    display: 'block',
    width: 12,
    height: 12,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
  usageLegendName: {
    overflow: 'hidden',
    display: 'inline-block',
    verticalAlign: 'bottom',
    maxWidth: 140,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.pxToRem(20),
    
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      whiteSpace: 'normal',
      fontSize: theme.typography.pxToRem(16),
    },
  },
  usageLegendValue: {
    display: 'inline-flex',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(24),

    '& span': {
      flexShrink: 0,
      flexBasis: 40,
    },
    
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2),
      fontSize: theme.typography.pxToRem(18),

      '& span': {
        flexBasis: 30,
      },
    },
  },
  usageNoData: {
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLOR_DARK_GRAY,
    fontSize: theme.typography.pxToRem(14),
  },
  usageInnerLabel: {
    position: 'absolute',
    textAlign: 'center',
    fontFamily: FONT_PROXIMA_NOVA,
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(24),
    lineHeight: 1.2,
    color: COLOR_TEXT,
    pointerEvents: 'none',
  },
  usageSideCard: {
    width: '100%',
    maxWidth: 320,
    backgroundColor: COLOR_WHITE,
    borderRadius: 12,
    border: `1px solid ${COLOR_BORDER}`,
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: FONT_PROXIMA_NOVA,
    minWidth: 0,

    [theme.breakpoints.down('sm')]: {
      maxWidth: 440,
    },
  },
  usageSideCardTop: {
    backgroundColor: COLOR_BACKGROUND_LIGHT,
    padding: theme.spacing(1.5, 2, 1.75),
    lineHeight: 1.1,
  },
  usageSideTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    color: COLOR_TEXT,
    marginBottom: theme.spacing(0.5),
    textAlign: 'center',
  },
  usageTotalPlays: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
    textAlign: 'center',
  },
  usageSection: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(2.5),
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.1,

    '&:last-of-type': {
      borderBottom: 0,
    },
  },
  usageSectionHeader: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: theme.spacing(1.5),
  },
  usageMetricGrid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2, 1),
  },
  usageMetricGrid5: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: theme.spacing(1),
  },
  usageMetricItem: {
    textAlign: 'center',
  },
  usageMetricValue: {
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
  },
  usageMetricLabel: {
    color: COLOR_TEXT,
  },
  divider: {
    width: '1px',
    height: '30px',
    margin: theme.spacing(0, 1),
    background: COLOR_MIDDLE_GRAY,
  },
  PFFGradesLoaderWrapper: {
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  PFFGradesEmptyState: {
    minHeight: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(14),
  },
  PFFGradesDialogContent: {
    overflow: 'auto',
    width: '96%',
    maxWidth: 1600,
    maxHeight: 'calc(100% - 40px)',
    borderRadius: 8,
  },
  careerDialogContent: {
    width: '40%',
    maxWidth: 1180,
  },
  dividedContent: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(.5, 1.5),

    '@media (max-width: 1680px)': {
      gap: theme.spacing(.5, 1),
    },
  },
  dividedContentSection: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1.5),
    borderRight: `1px solid ${COLOR_MIDDLE_GRAY}`,
    lineHeight: 1.1,

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },

    '@media (max-width: 1680px)': {
      paddingRight: theme.spacing(1),
    },
  },
  achievementsList: {
    width: '100%',
    overflow: 'hidden',
    borderTop: `1px solid ${COLOR_BORDER}`,
  },
  achievementRow: {
    display: 'grid',
    gridTemplateColumns: '88px 120px minmax(180px, 1.2fr) minmax(150px, 0.9fr)',
    alignItems: 'center',
    minHeight: 45,
    backgroundColor: COLOR_WHITE,
    borderBottom: `1px solid ${COLOR_BORDER}`,

    '&:nth-of-type(2n)': {
      backgroundColor: COLOR_BACKGROUND_WARM,
    },

    [MEDIA.MOBILE]: {
      gridTemplateColumns: '72px minmax(0, 1fr)',
      gridTemplateRows: 'auto auto auto',
    },
  },
  achievementCell: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '100%',
    minWidth: 0,
    padding: theme.spacing(1, 1.5),
    borderRight: `1px solid ${COLOR_BORDER}`,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    color: COLOR_TEXT,
    boxSizing: 'border-box' as const,
    overflowWrap: 'anywhere',

    '&:last-child': {
      borderRight: 0,
    },

    [MEDIA.MOBILE]: {
      padding: theme.spacing(1.25, 1.5),
      fontSize: theme.typography.pxToRem(15),
      borderRight: 0,
      borderBottom: `1px solid ${COLOR_BORDER}`,

      '&:last-child': {
        borderBottom: 0,
      },
    },
  },
  achievementIconCell: {
    justifyContent: 'center',

    [MEDIA.MOBILE]: {
      gridRow: '1 / span 3',
      borderRight: `1px solid ${COLOR_BORDER}`,
      borderBottom: 0,
    },
  },
  achievementYearCell: {
    fontSize: theme.typography.pxToRem(16),

    [MEDIA.MOBILE]: {
      gridColumn: 2,
      gridRow: 1,
    },
  },
  achievementTitleCell: {
    fontSize: theme.typography.pxToRem(16),

    [MEDIA.MOBILE]: {
      gridColumn: 2,
      gridRow: 2,
    },
  },
  achievementDetailCell: {
    fontSize: theme.typography.pxToRem(15),
    color: COLOR_DARK_BLUE,
    lineHeight: 1.35,

    [MEDIA.MOBILE]: {
      gridColumn: 2,
      gridRow: 3,
    },
  },
  achievementIconBadge: {
    width: 34,
    height: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 800,
    color: COLOR_TEXT,
    backgroundColor: COLOR_WHITE,
    border: `2px solid ${COLOR_TEXT}`,
    boxSizing: 'border-box' as const,
  },
  achievementIconAward: {
    clipPath: 'polygon(50% 0%, 62% 12%, 78% 8%, 82% 24%, 96% 30%, 88% 44%, 100% 56%, 86% 64%, 90% 80%, 74% 82%, 68% 98%, 50% 90%, 32% 98%, 26% 82%, 10% 80%, 14% 64%, 0% 56%, 12% 44%, 4% 30%, 18% 24%, 22% 8%, 38% 12%)',
    backgroundColor: '#ffd43b',
  },
  achievementIconAp: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 900,
    borderColor: '#e5e7eb',
    borderBottom: '5px solid #ff3b30',
  },
  achievementIconNfl: {
    borderRadius: 10,
    color: COLOR_WHITE,
    backgroundColor: COLOR_BLUE,
    borderColor: COLOR_BLUE,
  },
  achievementIconTrophy: {
    borderRadius: '50%',
    backgroundColor: COLOR_BACKGROUND_WARM,
    borderColor: COLOR_ORANGE,
    fontSize: theme.typography.pxToRem(15),
  },
  achievementIconStarter: {
    borderColor: COLOR_WHITE,
  },
  closeButton: {
    marginLeft: 'auto',
  }
}));

function renderAchievementIcon(classes: ReturnType<typeof useStyles>, icon: string) {
  switch (icon) {
    case 'award':
      return <span className={clsx(classes.achievementIconBadge, classes.achievementIconAward)} />;
    case 'AP':
      return <span className={clsx(classes.achievementIconBadge, classes.achievementIconAp)}>AP</span>;
    case 'NFL':
      return <span className={clsx(classes.achievementIconBadge, classes.achievementIconNfl)}>NFL</span>;
    case 'starter':
      return <span className={clsx(classes.achievementIconBadge, classes.achievementIconStarter)}><BannerIcon /></span>;
    default:
      return <span className={clsx(classes.achievementIconBadge, classes.achievementIconTrophy)}>🏆</span>;
  }
}

const DEFAULT_ACTIONS: ProductionTableAction[] = [
  { label: 'Profiler', icon: <ProfilerIcon />, isProfilerAction: true },
  { label: 'Rankings', icon: <RankingsIcon /> },
  { label: 'Usage', icon: <UsageIcon /> },
  { label: 'Career', icon: <AwardIcon /> },
  { label: 'Grades', icon: <PFFCircleIcon /> },
];

export default function CollegeNFLProductionTable(props: CollegeNFLProductionTableProps) {
  const {
    className,
    player,
    data,
    actions,
    playerUsageData,
    playerPFFGameLogData,
    isPlayerPFFGameLogLoading = false,
    playerCareerData,
    starterYear,
  } = props;
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<TabKey>('passing');
  const [isUsageDialogOpen, setIsUsageDialogOpen] = useState(false);
  const [isCareerDialogOpen, setIsCareerDialogOpen] = useState(false);
  const [isGradesDialogOpen, setIsGradesDialogOpen] = useState(false);
  const [profilerMenuAnchorEl, setProfilerMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [rankingMenuAnchorEl, setRankingMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredUsageYearKey, setHoveredUsageYearKey] = useState<string | null>(null);
  const [hoveredUsageIndex, setHoveredUsageIndex] = useState<number | null>(null);
  const profilerConfig = useSelector((state: State) => ({
    fbsProfilerMinYear: state.configurations.fbsProfilerMinYear,
    fcsProfilerMinYear: state.configurations.fcsProfilerMinYear,
    qbProfilerMinPassingAttempt: state.configurations.qbProfilerMinPassingAttempt,
    rusherProfilerMinRushingAttempt: state.configurations.rusherProfilerMinRushingAttempt,
    receiverProfilerMinReceptions: state.configurations.receiverProfilerMinReceptions,
    defenderProfilerMinDefensiveSnaps: state.configurations.defenderProfilerMinDefensiveSnaps,
  }));
  const playerGameLogData = playerPFFGameLogData || [];

  const primarySchool = ((player && player.highSchools) || [])
      .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);
  
  const primaryCollege = (player?.playerColleges || [])
      .find((college:College) => !!college?.isPrimary);

  const fullGameLogRows: PFFGameLogRow[] = useMemo(() => {
    if (!playerGameLogData || playerGameLogData.length === 0) return [];

    const sortedGameLogs = [...playerGameLogData].sort((a, b) => {
      const aTime = new Date(a.gameDate || a.createdAt || '').getTime();
      const bTime = new Date(b.gameDate || b.createdAt || '').getTime();
      const aIsValid = !Number.isNaN(aTime);
      const bIsValid = !Number.isNaN(bTime);

      if (aIsValid && bIsValid) {
        return aTime - bTime;
      }
      if (aIsValid) {
        return -1;
      }
      if (bIsValid) {
        return 1;
      }
      return 0;
    });

    return sortedGameLogs.map((row, idx) => {
      // const gameDate = row.gameDate ? new Date(row.gameDate) : null;
      // const formattedDate = gameDate && !Number.isNaN(gameDate.getTime())
      //   ? `${String(gameDate.getMonth() + 1).padStart(2, '0')}/${String(gameDate.getDate()).padStart(2, '0')}/${String(gameDate.getFullYear()).slice(-2)}`
      //   : '--/--/--';
      const formattedDate = formatDate(row.gameDate || null, true);
      return {
        id: String(row.gameId || idx),
        date: formattedDate,
        isHomeTeam: row.isHomeTeam,
        score: (row.teamScore != null && row.opponentTeamScore != null) ? `${row.teamScore}-${row.opponentTeamScore} ${row.teamScore == row.opponentTeamScore ? 'D' : (row.teamScore > row.opponentTeamScore) ? 'W': "L" }` : '',
        opponent: row.opponentTeam?.name || 'Unknown',
        opponentHref: row?.opponentTeam?.name
          ? `/team/${row.opponentTeam.name.toLowerCase().replace(/\s+/g, '-')}`
          : undefined,
        offensive: {
          overall: Number(row.offensiveGradesOverall) || 0,
          pass: Number(row.offensiveGradesPass) || 0,
          run: Number(row.offensiveGradesRun) || 0,
          rec: Number(row.offensiveGradesRec) || 0,
          runBlock: Number(row.offensiveGradesRunBlock) || 0,
          passBlock: Number(row.offensiveGradesPassBlock) || 0,
        },
        defensive: {
          overall: Number(row.defensiveGradesOverall) || 0,
          passRush: Number(row.defensiveGradesPassRush) || 0,
          runDef: Number(row.defensiveGradesRunDef) || 0,
          cover: Number(row.defensiveGradesCover) || 0,
        },
        snaps: {
          total: Number(row.snapsTotal) || 0,
          run: Number(row.snapsRun) || 0,
          pass: Number(row.snapsPass) || 0,
          rec: Number(row.snapsRec) || 0,
          runBlock: Number(row.snapsRunBlock) || 0,
          passBlock: Number(row.snapsPassBlock) || 0,
          passRush: Number(row.snapsPassRush) || 0,
          passDef: Number(row.snapsRunDef) || 0,
          cover: Number(row.snapsCover) || 0,
        },
      };
    });
  }, [playerGameLogData]);

  // const rows = data?.productionData ?? [];
  const rows = data || [];
  const profilerLinks = useMemo(
    () => buildPlayerProfilerLinks(player, rows, profilerConfig),
    [player, rows, profilerConfig],
  );
  const rankingLinks = useMemo(() => buildPlayerRankingLinks(player, rows), [player, rows]);

  const actionList = useMemo(() => {
    const baseActions = actions ?? DEFAULT_ACTIONS;

    return baseActions.filter((action) => {
      const isProfilerAction = action.isProfilerAction || action.label === 'QB Profiler' || action.label === 'Profiler';
      if (isProfilerAction) {
        // Hide profiler action when there is no qualifying profile.
        return Boolean(profilerLinks.primary);
      }

      const isRankingsAction = action.label === 'Rankings';
      if (isRankingsAction) {
        return Boolean(rankingLinks.primary);
      }

      return true;
    });
  }, [actions, profilerLinks.primary, rankingLinks.primary]);
  
  const columns = useMemo(() => [...COMMON_COLS, ...TAB_COLS[activeTab]], [activeTab]);
  const totalsRow = useMemo(() => (rows.length ? computeTotals(rows, columns) : null), [rows, columns]);
  const displayRows = useMemo(() => (totalsRow ? [...rows, totalsRow] : rows), [rows, totalsRow]);
  const { sortKey, sortDirection, handleSort, sortedRows } = useTableSort<CollegeProductionRow | TotalsRow, string>({
    rows: displayRows,
    initialSortKey: null,
    initialDirection: 'asc',
    nextKeyDirection: 'asc',
    sortRows: (list, key, direction) => {
      if (!rows.length) {
        return list;
      }

      const isTotals = (r: CollegeProductionRow | TotalsRow) => (r as TotalsRow)._isTotals;
      const dataRows = list.filter((r) => !isTotals(r)) as CollegeProductionRow[];
      const tot = list.find(isTotals);
      const sorted = [...dataRows].sort((a, b) => {
        const aVal = getCellValue(a, key);
        const bVal = getCellValue(b, key);
        const an = Number(aVal);
        const bn = Number(bVal);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return direction === 'asc' ? an - bn : bn - an;
        const comparison = String(aVal ?? '').trim().localeCompare(String(bVal ?? '').trim(), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        return direction === 'asc' ? comparison : -comparison;
      });

      return tot ? [...sorted, tot] : sorted;
    },
  });

  function getStickyColumnClass(colIndex: number) {
      return colIndex === 0 ? classes.stickyCol0 : null;
  }

  const renderCell = (row: CollegeProductionRow | TotalsRow, col: ColDef, colIndex: number, rowIndex: number) => {
    const isTotals = (row as TotalsRow)._isTotals;
    const isStickyColumn = colIndex === 0;
    const isAltRow = !isTotals && rowIndex % 2 === 1;
    const stickyColClass = getStickyColumnClass(colIndex);

    if (isTotals && col.key === 'year') {
      return (
        <td
          key={String(col.key)}
          className={clsx(
            classes.td,
            classes.tdFirst,
            isStickyColumn && classes.tdSticky,
            isStickyColumn && classes.tdStickyTotals,
            stickyColClass,
          )}
        />
      );
    }

    const raw = getCellValue(row, col.key as string);
    const num = typeof raw === 'number' ? raw : Number(raw);
    const display = col.format != null ? formatCellValue(num, col) : (raw === '—' ? '—' : String(raw));
    const highlight = col.highlightRel && typeof raw === 'number' && !isTotals;
    return (
      <td
        key={String(col.key)}
        className={clsx(
          classes.td,
          col.key === 'year' && classes.tdFirst,
          col.key === 'teamName' && classes.tdSecond,
          isStickyColumn && classes.tdSticky,
          isStickyColumn && isAltRow && classes.tdStickyAlt,
          isStickyColumn && isTotals && classes.tdStickyTotals,
          stickyColClass,
          highlight && classes.cellHighlight,
        )}
      >
        {display}
      </td>
    );
  };

  function handleActionClick(
    event: React.MouseEvent<HTMLAnchorElement | HTMLSpanElement | HTMLButtonElement>,
    action: ProductionTableAction,
  ) {
    if (action.disabled) {
      event.preventDefault();
      return;
    }

    // Allow explicit onClick from props to override default behaviour
    if (action.onClick) {
      event.preventDefault();
      action.onClick();
      return;
    }

    if (action.label === 'Usage') {
      event.preventDefault();
      setIsUsageDialogOpen(true);
      return;
    }

    if (action.label === 'Career') {
      event.preventDefault();
      setIsCareerDialogOpen(true);
      return;
    }

    if (action.label === 'Grades') {
      event.preventDefault();
      setIsGradesDialogOpen(true);
    }
  }

  function openProfilerLink(link: ProfilerLink) {
    window.open(link.url, '_blank', 'noopener,noreferrer');
  }

  function openProfilerDropdown(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setProfilerMenuAnchorEl(event.currentTarget);
  }

  function closeProfilerDropdown() {
    setProfilerMenuAnchorEl(null);
  }

  function openRankingLink(link: RankingLink) {
    window.open(link.url, '_blank', 'noopener,noreferrer');
  }

  function openRankingDropdown(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setRankingMenuAnchorEl(event.currentTarget);
  }

  function closeRankingDropdown() {
    setRankingMenuAnchorEl(null);
  }

  const baseUsageColors = [
    '#0f89e6',
    '#f47321',
    '#5eba6d',
    '#8b5cf6',
    '#ef4444',
    '#14b8a6',
    '#f59e0b',
    '#6366f1',
    '#10b981',
    '#e11d48',
    '#0ea5e9',
    '#84cc16',
  ];

  const getUsageColors = (count: number): string[] => (
    Array.from({ length: count }, (_, index) => {
      if (index < baseUsageColors.length) {
        return baseUsageColors[index];
      }

      const hue = Math.round((index * 137.508) % 360);
      return `hsl(${hue}, 72%, 48%)`;
    })
  );

  const percent = (value: number, denominator: number) => {
    if (!denominator) return '0.0';
    return ((Number(value || 0) / denominator) * 100).toFixed(1);
  };

  const usageContent = () => {
    if (!playerUsageData?.length) {
      return <div className={classes.usageNoData}>No usage data available.</div>;
    }

    return (
      <div className={classes.usageYearsContainer}>
        {playerUsageData
          .sort((a, b) => b.year - a.year)
          .map((usageYear) => {
            const usageYearKey = `${usageYear.year}-${usageYear.side || 'UNKNOWN'}`;
            const sideLabel = usageYear.side
              ? `${usageYear.side.charAt(0)}${usageYear.side.slice(1).toLowerCase()} `
              : '';
            const usagePieData = (usageYear.alignments || [])
              .map((alignment) => ({
                name: alignment.name,
                value: Number(alignment.plays) || 0,
              }))
              .filter((item) => item.value > 0)
              .sort((a, b) => b.value - a.value);
            const usageYearColors = getUsageColors(usagePieData.length || 1);

            const total = usageYear.totalPlays || usagePieData.reduce((sum, item) => sum + item.value, 0) || 1;
            const slotOutsideTotal = Number(usageYear.slotOutsideTotal || 0);
            const leftRightSideTotal = Number(usageYear.leftRightSideTotal || 0);
            const rushCoverageTotal = Number(usageYear.rushCoverageTotal || 0);
            const tackleGuardCenterTotal = Number(usageYear.tackleGuardCenterTotal || 0);
            const boxPlayerTotalCal = usageYear.boxPlayerTotal || (usageYear.alignments || []).reduce(
              (sum, alignment) => sum + (Number(alignment.boxPlayer) || 0),
              0,
            );
            const boxPlayerTotalYes = percent(boxPlayerTotalCal, total);
            const boxPlayerTotalNo = (100 - Number(boxPlayerTotalYes)).toFixed(1);
            const hasHoveredLegendRow = hoveredUsageYearKey === usageYearKey && hoveredUsageIndex !== null;

            const handleUsageHover = (index: number | null) => {
              if (index === null) {
                setHoveredUsageYearKey(null);
                setHoveredUsageIndex(null);
                return;
              }

              setHoveredUsageYearKey(usageYearKey);
              setHoveredUsageIndex(index);
            };

            return (
              <div key={`${usageYear.year}-${usageYear.side}`} className={classes.usageYearBlock}>
                <div className={classes.usageYearTitle}>
                  {usageYear.year} PFF {sideLabel}Player Usage & Alignment
                </div>

                <div className={classes.usageRoot}>
                  <div className={classes.usageChartWrapper}>
                    <div className={classes.usageChart}>
                      <PieChartComponent
                        data={usagePieData.length ? usagePieData : [{ name: 'No Data', value: 1 }]}
                        colors={usagePieData.length ? usageYearColors : [COLOR_MIDDLE_GRAY]}
                        activeIndex={hoveredUsageYearKey === usageYearKey ? hoveredUsageIndex : null}
                        onSliceHover={handleUsageHover}
                      />
                      <div className={classes.usageInnerLabel}>
                        <div>Player</div>
                        <div>Positioning</div>
                      </div>
                    </div>

                    <div className={classes.usageLegend}>
                      {(usagePieData).map((item, index) => {
                        const percent = (item.value / total) * 100;
                        const isHighlighted = hoveredUsageYearKey === usageYearKey && hoveredUsageIndex === index;
                        const isDimmed = hasHoveredLegendRow && !isHighlighted;
                        return (
                          <div
                            key={item.name}
                            className={clsx(
                              classes.usageRow,
                              isHighlighted && classes.usageRowHighlighted,
                              isDimmed && classes.usageRowDimmed,
                            )}
                            onMouseEnter={() => handleUsageHover(index)}
                            onMouseLeave={() => handleUsageHover(null)}
                          >
                            <span className={classes.usageRowLabel}>
                              <span
                                className={classes.usageLegendDot}
                                style={{ backgroundColor: usageYearColors[index] }}
                              />
                              <Tooltip title={item.name} placement="top">
                                <span className={classes.usageLegendName}>{item.name}</span>
                              </Tooltip>
                              {/* <span className={classes.usageLegendName}>{item.name}</span> */}
                            </span>
                            <span className={classes.usageLegendValue}>
                              <span>{percent.toFixed(1)}%</span>
                              <span>{item.value}</span>
                            </span>
                          </div>
                        );
                      })}

                      {!usagePieData.length && (
                        <div className={classes.usageRow}>
                          <span className={classes.usageRowLabel}>No alignment data</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={classes.usageSideCard}>
                    <div className={classes.usageSideCardTop}>
                      <div className={classes.usageSideTitle}>Total {sideLabel.trim() || 'Player'} Plays</div>
                      <div className={classes.usageTotalPlays}>{usageYear.totalPlays || 0}</div>
                    </div>

                    {
                      usageYear.side === 'DEFENSIVE' && (
                        <>
                        <div className={classes.usageSection}>
                          <div className={classes.usageSectionHeader}>Pass Responsibility</div>
                          <div className={classes.usageMetricGrid2}>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.rushedTotal || 0), rushCoverageTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Rush</div>
                            </div>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.coverageTotal || 0), rushCoverageTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Coverage</div>
                            </div>
                          </div>
                        </div>
                        <div className={classes.usageSection}>
                          <div className={classes.usageSectionHeader}>Box Player</div>
                          <div className={classes.usageMetricGrid2}>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{boxPlayerTotalYes}%</div>
                              <div className={classes.usageMetricLabel}>Yes</div>
                            </div>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{boxPlayerTotalNo}%</div>
                              <div className={classes.usageMetricLabel}>No</div>
                            </div>
                          </div>
                        </div>
                        </>
                        
                      )
                    }

                    {
                      usageYear.side === 'OFFENSIVE' && (
                        <>
                        <div className={classes.usageSection}>
                          <div className={classes.usageSectionHeader}>Receiver</div>
                          <div className={classes.usageMetricGrid2}>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.slotTotal || 0), slotOutsideTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Slot</div>
                            </div>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.outsideTotal || 0), slotOutsideTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Outside</div>
                            </div>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.leftSideTotal || 0), leftRightSideTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Left</div>
                            </div>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.rightSideTotal || 0), leftRightSideTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Right</div>
                            </div>
                          </div>
                        </div>
                        <div className={classes.usageSection}>
                          <div className={classes.usageSectionHeader}>OL Alignment</div>
                            <div className={classes.usageMetricGrid5}>
                              <div className={classes.usageMetricItem}>
                                <div className={classes.usageMetricValue}>{percent(Number(usageYear.leftTackleTotal || 0), tackleGuardCenterTotal)}%</div>
                                <div className={classes.usageMetricLabel}>LT</div>
                              </div>
                              <div className={classes.usageMetricItem}>
                                <div className={classes.usageMetricValue}>{percent(Number(usageYear.leftGuardTotal || 0), tackleGuardCenterTotal)}%</div>
                                <div className={classes.usageMetricLabel}>LG</div>
                              </div>
                              <div className={classes.usageMetricItem}>
                                <div className={classes.usageMetricValue}>{percent(Number(usageYear.centerTotal || 0), tackleGuardCenterTotal)}%</div>
                                <div className={classes.usageMetricLabel}>C</div>
                              </div>
                              <div className={classes.usageMetricItem}>
                                <div className={classes.usageMetricValue}>{percent(Number(usageYear.rightGuardTotal || 0), tackleGuardCenterTotal)}%</div>
                                <div className={classes.usageMetricLabel}>RG</div>
                              </div>
                              <div className={classes.usageMetricItem}>
                                <div className={classes.usageMetricValue}>{percent(Number(usageYear.rightTackleTotal || 0), tackleGuardCenterTotal)}%</div>
                                <div className={classes.usageMetricLabel}>RT</div>
                              </div>
                            </div>
                        </div>
                        </>
                      )
                    }

                    {
                      usageYear.side !== 'OFFENSIVE' && usageYear.side !== 'DEFENSIVE' && (
                        <div className={classes.usageSection}>
                          <div className={classes.usageSectionHeader}>Left vs Right</div>
                          <div className={classes.usageMetricGrid2}>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.leftSideTotal || 0), leftRightSideTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Left</div>
                            </div>
                            <div className={classes.usageMetricItem}>
                              <div className={classes.usageMetricValue}>{percent(Number(usageYear.rightSideTotal || 0), leftRightSideTotal)}%</div>
                              <div className={classes.usageMetricLabel}>Right</div>
                            </div>
                          </div>
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const careerContent = () => {

    const achievements: CareerAchievement[] = [];
    if (playerCareerData?.playerAwards) {
      if (playerCareerData?.playerAwards?.allConference) {
        playerCareerData?.playerAwards?.allConference?.map((award: number) => {
          achievements.push({
            year: `${award}`,
            icon: 'award',
            title: 'All Conference',
            detail: '',
          });
        });
      }
      if (playerCareerData?.playerAwards?.allAmerican) {
        playerCareerData?.playerAwards?.allAmerican?.map((award: number) => {
          achievements.push({
            year: `${award}`,
            icon: 'AP',
            title: 'All American',
            detail: '',
          });
        });
      }
      if (playerCareerData?.playerAwards?.allPro) {
        playerCareerData?.playerAwards?.allPro?.map((award: number) => {
          achievements.push({
            year: `${award}`,
            icon: 'trophy',
            title: 'All Pro',
            detail: '',
          });
        });
      }
      if (playerCareerData?.playerAwards?.proBowl) {
        playerCareerData?.playerAwards?.proBowl?.map((award: number) => {
          achievements.push({
            year: `${award}`,
            icon: 'trophy',
            title: 'Pro Bowl',
            detail: '',
          });
        });
      }
    }
    if (starterYear) {
      achievements.push({
        year: `${starterYear}-Year`,
        icon: 'starter',
        title: 'NCAA Starter',
        detail: '',
      });
    }

    const HSPrimaryPosition = primarySchool?.positions?.find((pos) => pos.isPrimary)?.code;
    const collegePrimaryPosition = primaryCollege?.positions?.find((pos) => pos.isPrimary)?.code;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <NamedBox name="Career"
          headerContent={<CloseButton className={classes.closeButton} onClick={() => setIsCareerDialogOpen(false)} />}
          white>
          <ListItem
            icon={SchoolIcon}
            title='High School:'
          >
            <div><b>{primarySchool?.graduatingClass} Grad - {primarySchool?.name} ({HSPrimaryPosition})</b></div>
          </ListItem>
          {
            playerCareerData?.playerYearlyCollegeRoster?.length && playerCareerData?.playerYearlyCollegeRoster?.map((college: any, idx: number) => {
              return (
                <ListItem
                  key={idx}
                  icon={ClassIcon}
                  title={`College (${college?.year}):`}
                >
                  <div>
                    <img
                      src={college?.logo247 || college?.logoESPN}
                      alt={college.teamName}
                      style={{ width: 24, height: 24, marginRight: 8, verticalAlign: 'middle' }}
                    />
                    <b>{college.teamName} {college?.class} {college?.position ? `(${college.position})` : ''}</b>
                    </div>
                </ListItem>
              );
            })
          }
          <ListItem
            icon={TransferPortalIcon}
            title='Transfer Portal:'
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <div><b>Entered: {playerCareerData?.playerTransferPortal?.transferYear || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Status: {capitalizeFirstLetter(playerCareerData?.playerTransferPortal?.transferStatus || '') || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Grad: {playerCareerData?.playerTransferPortal?.graduateStudent ? 'Yes' : 'No'}</b></div>
              </div>
            </div>
          </ListItem>
          <div style={{ marginLeft: 18 }}>
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <div><b>NCAA ID: {playerCareerData?.playerTransferPortal?.ncaaId || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Division: {playerCareerData?.playerTransferPortal?.division || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Year: {playerCareerData?.playerTransferPortal?.transferYear || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Aid: {playerCareerData?.playerTransferPortal?.athleticAid || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>DSA: {playerCareerData?.playerTransferPortal?.designatedSaSports || <NotAvailable />}</b></div>
              </div>
            </div>
          </div>
          {/* <ListItem
            title={<b>NCAA ID: {playerCareerData?.playerTransferPortal?.ncaaId || <NotAvailable />}</b>}
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <div><b>Division: {playerCareerData?.playerTransferPortal?.division || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Year: {playerCareerData?.playerTransferPortal?.transferYear || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Aid: {playerCareerData?.playerTransferPortal?.athleticAid || <NotAvailable />}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>DSA: {playerCareerData?.playerTransferPortal?.designatedSaSports || <NotAvailable />}</b></div>
              </div>
            </div>

          </ListItem> */}
        </NamedBox>

        <NamedBox name="Achievements" white>
          <div className={classes.achievementsList}>
            {achievements.map((achievement, index) => (
              <div key={`${achievement.year}-${achievement.title}-${index}`} className={classes.achievementRow}>
                <div className={clsx(classes.achievementCell, classes.achievementIconCell)}>
                  {renderAchievementIcon(classes, achievement.icon)}
                </div>
                <div className={clsx(classes.achievementCell, classes.achievementYearCell)}>{achievement.year}</div>
                <div className={clsx(classes.achievementCell, classes.achievementTitleCell)}>{achievement.title}</div>
                <div className={clsx(classes.achievementCell, classes.achievementDetailCell)}>{achievement.detail || '—'}</div>
              </div>
            ))}
          </div>
        </NamedBox>
      </div>
    );
  };

  const headerContent = (
    <div className={classes.headerInner}>
      <div className={classes.labelAndTabs}>
        {(['passing', 'rushing', 'receiving', 'defense'] as TabKey[]).map((tab) => (
          <button
            key={tab}
            type="button"
            className={clsx(classes.tab, activeTab === tab && classes.tabActive)}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      <div className={classes.actions}>
        {actionList.map((action, idx) => {
          const isProfilerAction = action.isProfilerAction || action.label === 'QB Profiler' || action.label === 'Profiler';
          if (isProfilerAction && profilerLinks.primary) {
            return (
              <span key={idx} className={classes.profilerActionWrap}>
                <span
                  role="button"
                  className={classes.actionLink}
                  onClick={() => openProfilerLink(profilerLinks.primary as ProfilerLink)}
                >
                  {action.icon && <span className={classes.actionIcon}>{action.icon}</span>}
                  <span className={classes.actionLabel}>{profilerLinks.primary.label}</span>
                </span>

                {profilerLinks.additional.length > 0 && (
                  <button
                    type="button"
                    aria-label="Open additional profilers"
                    className={classes.profilerMenuTrigger}
                    onClick={openProfilerDropdown}
                  >
                    <ArrowDropDownIcon className={classes.actionIcon} />
                  </button>
                )}
              </span>
            );
          }

          const isRankingsAction = action.label === 'Rankings';
          if (isRankingsAction && rankingLinks.primary) {
            return (
              <span key={idx} className={classes.profilerActionWrap}>
                <span
                  role="button"
                  className={classes.actionLink}
                  onClick={() => openRankingLink(rankingLinks.primary as RankingLink)}
                >
                  {action.icon && <span className={classes.actionIcon}>{action.icon}</span>}
                  <span className={classes.actionLabel}>{rankingLinks.primary.label}</span>
                </span>

                {rankingLinks.additional.length > 0 && (
                  <button
                    type="button"
                    aria-label="Open additional rankings"
                    className={classes.profilerMenuTrigger}
                    onClick={openRankingDropdown}
                  >
                    <ArrowDropDownIcon className={classes.actionIcon} />
                  </button>
                )}
              </span>
            );
          }

          const content = (
            <>
              {action.icon && <span className={classes.actionIcon}>{action.icon}</span>}
              <span className={classes.actionLabel}>{action.label}</span>
            </>
          );
          const cls = clsx(classes.actionLink, action.disabled && classes.actionLinkDisabled);
          return (
            <span
              key={idx}
              role="button"
              className={cls}
              onClick={(event) => handleActionClick(event, action)}
            >
              {content}
            </span>
          );
        })}

        <Menu
          anchorEl={profilerMenuAnchorEl}
          keepMounted
          open={Boolean(profilerMenuAnchorEl)}
          onClose={closeProfilerDropdown}
        >
          {profilerLinks.additional.map((link) => (
            <MenuItem
              key={link.type}
              className={classes.profilerMenuItem}
              onClick={() => {
                closeProfilerDropdown();
                openProfilerLink(link);
              }}
            >
              {link.label}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={rankingMenuAnchorEl}
          keepMounted
          open={Boolean(rankingMenuAnchorEl)}
          onClose={closeRankingDropdown}
        >
          {rankingLinks.additional.map((link) => (
            <MenuItem
              key={link.type}
              className={classes.profilerMenuItem}
              onClick={() => {
                closeRankingDropdown();
                openRankingLink(link);
              }}
            >
              {link.label}
            </MenuItem>
          ))}
        </Menu>
      </div>
    </div>
  );

  return (
    <>
      <NamedBox
        className={clsx(classes.root, className)}
        headerClassName={classes.headerClassName}
        headerNameClassName={classes.headerName}
        name="Yearly Stats"
        headerContent={headerContent}
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead className={classes.thead}>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={String(col.key)}
                    className={clsx(
                      classes.th,
                      col.key === 'year' && classes.thFirst,
                      col.key === 'teamName' && classes.thSecond,
                      idx === 2 && classes.thThird,
                      idx === 3 && classes.thFourth,
                      idx === 0 && classes.thSticky,
                      idx === 0 && getStickyColumnClass(idx),
                      classes.sortableHeader,
                    )}
                  >
                    <TableSortLabel
                      active={sortKey === col.key}
                      direction={sortKey === col.key ? sortDirection : 'asc'}
                      hideSortIcon={false}
                      IconComponent={(iconProps) => (
                        <SortArrowIcon
                          {...iconProps}
                          style={{
                            color: sortKey === col.key ? COLOR_BLUE : COLOR_DARK_GRAY,
                          }}
                        />
                      )}
                      classes={{
                        root: classes.sortLabel,
                        icon: classes.sortLabelIcon,
                      }}
                      onClick={() => handleSort(col.key as string)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, index) => {
                const isTotals = (row as TotalsRow)._isTotals;
                return (
                  <tr
                    key={isTotals ? 'totals' : `${row.year}-${row.teamName}-${index}`}
                    className={clsx(
                      classes.bodyRow,
                      !isTotals && index % 2 === 1 && classes.bodyRowAlt,
                      isTotals && classes.totalsRow,
                    )}
                  >
                    {columns.map((col, colIndex) => renderCell(row, col, colIndex, index))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </NamedBox>

      <Dialog
        open={isUsageDialogOpen}
        title="Usage"
        onClose={() => setIsUsageDialogOpen(false)}
        contentClassName={classes.usageDialog}
        noContentPadding={true}
      >
        {usageContent()}
      </Dialog>

      <Dialog
        title=''
        open={isCareerDialogOpen}
        onClose={() => setIsCareerDialogOpen(false)}
        contentClassName={classes.careerDialogContent}
        noContentPadding={true}
        noHeader={true}
      >
        {careerContent()}
      </Dialog>

      <Dialog
        open={isGradesDialogOpen}
        title="PFF Grades"
        onClose={() => setIsGradesDialogOpen(false)}
        noContentPadding={true}
        containsLongContent
        contentClassName={classes.PFFGradesDialogContent}
      >
        {isPlayerPFFGameLogLoading ? (
          <Box className={classes.PFFGradesLoaderWrapper}>
            <CircularProgress size={28} />
          </Box>
        ) : fullGameLogRows.length > 0 ? (
          <PFFGameGradeLogTable useNamedBox={false} rows={fullGameLogRows} />
        ) : (
          <Box className={classes.PFFGradesEmptyState}>No data available.</Box>
        )}
      </Dialog>
    </>
  );
}
